import {
  ContextMenu,
  ContextMenuPopup,
  ContextMenuTrigger,
  Icon,
  type IconName,
  Menu,
  MenuPopup,
  MenuTrigger,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import React, { useCallback } from "react";

import {
  CONTEXT_MENU_PARTS,
  DROPDOWN_MENU_PARTS,
  type MenuParts,
} from "#app/components/menus/menu-parts";
import { copyText } from "#app/lib/utils/copy-actions";

/**
 * One favorited row in a module-menu Favorites section. Entity-agnostic — an
 * id and a display name are all it renders, so issues and Accounts share it.
 * `icon` is the row's leading glyph: sections that mix kinds (Home) pass the
 * kind glyph so an Account row reads as an Account; omitted, the row wears the
 * favorites star. `visual` replaces that glyph outright for a kind whose
 * identity is a live entity rather than an icon (a project's stage pie).
 * `copyUrl` is the entity's deep link; an entity with no URL (an Account opens
 * in the global overlay) omits it and gets no "Copy link" action, because a
 * fabricated link would 404.
 *
 * `items` hands the row a kind's SHARED item roster (the project menu every
 * board card and table row uses). Given one, the row's ⋯ opens it and the row
 * itself becomes a right-click trigger for the same roster, so a favorite
 * offers the same actions as the entity anywhere else. Without it the row
 * falls back to the two actions it can serve on its own — copy link, and
 * remove from favorites — behind the same two doors, so a held press on a
 * coarse pointer (where the ⋯ never shows) reaches them too.
 */
export function FavoriteItem({
  id,
  name,
  icon = "star",
  visual,
  isActive,
  onClick,
  copyUrl,
  onRemove,
  items,
  actionLabel = "Favorite actions",
  tooltip,
}: Omittable<{
  id: string;
  name: string;
  /** The row's full text when `name` is an abbreviation of it. */
  tooltip?: string;
  icon?: IconName;
  visual?: React.ReactNode;
  isActive: boolean;
  onClick: () => void;
  copyUrl?: string;
  /** Omitted by a row whose `items` roster already carries an unfavorite. */
  onRemove?: (id: string) => void;
  items?: (parts?: MenuParts) => React.ReactNode;
  actionLabel?: string;
}>) {
  const handleRemove = useCallback(() => {
    onRemove?.(id);
  }, [onRemove, id]);

  const handleCopyLink = useCallback(() => {
    if (copyUrl) copyText(copyUrl, "Link");
  }, [copyUrl]);

  // One roster for both doors: the shared kind roster when the row has one,
  // otherwise the two actions the row serves itself. Rendered through the
  // menu kind's own parts — a context menu and a dropdown are different
  // components in Base UI, not one component with a prop.
  const hasActions = items != null || copyUrl != null || onRemove != null;
  const renderItems = (parts: MenuParts): React.ReactNode =>
    items ? (
      items(parts)
    ) : (
      <>
        {copyUrl ? (
          <parts.Item onClick={handleCopyLink}>
            <Icon name="link" size="md" />
            <Text>Copy link</Text>
          </parts.Item>
        ) : null}
        {onRemove ? (
          <parts.Item onClick={handleRemove}>
            <Icon name="star-filled" size="md" />
            <Text>Remove from favorites</Text>
          </parts.Item>
        ) : null}
      </>
    );

  // The row button IS the right-click trigger, not a wrapper around it: the
  // trailing ⋯ action centers itself and takes its ink off `peer/menu-button`
  // selectors, which only resolve while the two are siblings.
  const rowButton = (
    <SidebarMenuButton isActive={isActive} onClick={onClick}>
      {visual ?? <Icon name={icon} />}
      <Text truncate>{name}</Text>
    </SidebarMenuButton>
  );

  // With a tooltip the row button is ALSO the tooltip trigger, composed
  // through `render` so the context-menu trigger and the tooltip share one
  // node — the row keeps its right-click and its ⋯ sibling either way.
  const triggerRow = hasActions ? <ContextMenuTrigger render={rowButton} /> : rowButton;
  const row = tooltip ? (
    <Tooltip>
      <TooltipTrigger render={triggerRow} />
      <TooltipPopup side="right">{tooltip}</TooltipPopup>
    </Tooltip>
  ) : (
    triggerRow
  );

  return (
    <SidebarMenuItem>
      {hasActions ? (
        <ContextMenu>
          {row}
          <ContextMenuPopup width="action">
            {renderItems(CONTEXT_MENU_PARTS)}
          </ContextMenuPopup>
        </ContextMenu>
      ) : (
        row
      )}
      {hasActions ? (
        <Menu>
          <MenuTrigger
            render={<SidebarMenuAction aria-label={actionLabel} showOnHover />}
          >
            <Icon color="muted" name="ellipsis" size="sm" />
          </MenuTrigger>
          <MenuPopup
            align="start"
            side="right"
            width={items ? "action" : undefined}
          >
            {renderItems(DROPDOWN_MENU_PARTS)}
          </MenuPopup>
        </Menu>
      ) : null}
    </SidebarMenuItem>
  );
}
